import { BarChart3Icon, BriefcaseIcon, HistoryIcon, LifeBuoyIcon, LogOutIcon, MonitorIcon, MoonIcon, PanelLeftIcon, PlusIcon, SendIcon, SettingsIcon, SunIcon, UserIcon, UsersIcon } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { JobMark } from '@/components/JobMark';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut } from '@/components/ui/command';
import { useAuth, useMe } from '@/lib/auth';
import { useSidebar } from '@/lib/sidebar';
import { useTheme } from '@/lib/theme';

interface PaletteContext {
  open: boolean;
  setOpen: (o: boolean) => void;
}

const Ctx = createContext<PaletteContext | null>(null);

export function useCommandPalette(): PaletteContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCommandPalette must be used inside CommandPaletteProvider');
  return ctx;
}

const SECTIONS = [
  { to: 'overview', label: 'Overview' },
  { to: 'contacts', label: 'Contacts' },
  { to: 'compose', label: 'Compose' },
  { to: 'history', label: 'History' },
  { to: 'keywords', label: 'Keywords', feature: 'keywords' as const },
  { to: 'automation', label: 'Automation', feature: 'automation' as const },
  { to: 'team', label: 'Team' },
  { to: 'settings', label: 'Settings' },
];

/** ⌘K / Ctrl+K: jump to any job, section or page. Mounted once inside the signed-in shell. */
export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const value = useMemo(() => ({ open, setOpen }), [open]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <Palette />
    </Ctx.Provider>
  );
}

function Palette() {
  const { open, setOpen } = useCommandPalette();
  const me = useMe();
  const { logout } = useAuth();
  const { setTheme } = useTheme();
  const sidebar = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');

  const go = useCallback(
    (to: string) => {
      setOpen(false);
      setQuery('');
      navigate(to);
    },
    [navigate, setOpen],
  );

  const currentJobId = location.pathname.match(/^\/jobs\/([^/]+)/)?.[1];
  const currentJob = me.jobs.find((j) => j.id === currentJobId);
  const activeJobs = me.jobs.filter((j) => j.status === 'active');
  const f = me.tenant.features;
  const sections = SECTIONS.filter((s) => !s.feature || (s.feature === 'keywords' ? f.keywords : f.weatherAutomation || f.scheduledSends));

  return (
    <CommandDialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <CommandInput placeholder="Jump to a job, section or page…" value={query} onValueChange={setQuery} />
      <CommandList>
        <CommandEmpty>Nothing matches.</CommandEmpty>

        {currentJob ? (
          <CommandGroup heading={`${currentJob.name} · sections`}>
            {sections.map((s) => (
              <CommandItem key={s.to} value={`${currentJob.name} ${s.label} section`} onSelect={() => go(`/jobs/${currentJob.id}/${s.to}`)}>
                {s.to === 'compose' ? <SendIcon /> : s.to === 'history' ? <HistoryIcon /> : s.to === 'team' ? <UsersIcon /> : s.to === 'settings' ? <SettingsIcon /> : <BriefcaseIcon />}
                <span>{s.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        <CommandGroup heading="Jobs">
          {activeJobs.map((j) => (
            <CommandItem key={j.id} value={`job ${j.name}`} onSelect={() => go(`/jobs/${j.id}/overview`)}>
              <JobMark job={j} size="xs" />
              <span className="truncate">{j.name}</span>
              <span className="text-muted-foreground ml-auto text-xs">{j.myRole === 'admin' ? 'Admin' : 'User'}</span>
            </CommandItem>
          ))}
          <CommandItem value="all jobs list" onSelect={() => go('/jobs')}>
            <BriefcaseIcon />
            <span>All jobs</span>
          </CommandItem>
          {me.permissions.createJobs ? (
            <CommandItem value="new job create" onSelect={() => go('/jobs?new=1')}>
              <PlusIcon />
              <span>New job…</span>
            </CommandItem>
          ) : null}
        </CommandGroup>

        {me.permissions.manageUsers ? (
          <CommandGroup heading="Organization">
            <CommandItem value="organization users people" onSelect={() => go('/org/users')}>
              <UsersIcon />
              <span>Users</span>
            </CommandItem>
            <CommandItem value="organization usage reports" onSelect={() => go('/org/reports')}>
              <BarChart3Icon />
              <span>Usage</span>
            </CommandItem>
            <CommandItem value="organization help requests support" onSelect={() => go('/org/support')}>
              <LifeBuoyIcon />
              <span>Help requests</span>
            </CommandItem>
            <CommandItem value="organization settings" onSelect={() => go('/org/settings')}>
              <SettingsIcon />
              <span>Organization settings</span>
            </CommandItem>
          </CommandGroup>
        ) : null}

        <CommandGroup heading="You">
          <CommandItem value="account profile password" onSelect={() => go('/account')}>
            <UserIcon />
            <span>Account</span>
          </CommandItem>
          <CommandItem value="help support" onSelect={() => go('/help')}>
            <LifeBuoyIcon />
            <span>Help</span>
          </CommandItem>
          <CommandItem
            value="toggle sidebar collapse"
            onSelect={() => {
              sidebar.toggle();
              setOpen(false);
            }}
          >
            <PanelLeftIcon />
            <span>Toggle sidebar</span>
            <CommandShortcut>[</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="theme light"
            onSelect={() => {
              setTheme('light');
              setOpen(false);
            }}
          >
            <SunIcon />
            <span>Light theme</span>
          </CommandItem>
          <CommandItem
            value="theme dark"
            onSelect={() => {
              setTheme('dark');
              setOpen(false);
            }}
          >
            <MoonIcon />
            <span>Dark theme</span>
          </CommandItem>
          <CommandItem
            value="theme system"
            onSelect={() => {
              setTheme('system');
              setOpen(false);
            }}
          >
            <MonitorIcon />
            <span>Follow system theme</span>
          </CommandItem>
          <CommandItem
            value="sign out logout"
            onSelect={() => {
              setOpen(false);
              void logout().then(() => navigate('/login'));
            }}
          >
            <LogOutIcon />
            <span>Sign out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
